/**
 * Keyboard device with tap latching: a key pressed and released between two samples still shows as held for
 * one tick. Listens on an injected event target (web: `window`, capture phase) and ignores key repeats. Several
 * KeyboardDevices (solo / p1 / p2) share one KeyboardState. Key events are KeyboardEvent-SHAPED (`code`, `repeat`,
 * `target`): a native shell feeds hardware-keyboard events through the same interface.
 */
import { BUTTON_NAMES, type InputFrame } from '@hf/shared';
import { cloneBindings } from './bindings';
import type { Bindings, InputDevice } from './types';

/** The part of a KeyboardEvent the input code reads (web: the DOM event itself). */
export interface KeyEventLike {
  readonly code: string;
  readonly repeat: boolean;
  /** The focused element (text fields keep their keys); anything without a `tagName` is not a text field. */
  readonly target: unknown;
  readonly defaultPrevented?: boolean;
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly altKey?: boolean;
  preventDefault?(): void;
}

/** An EventTarget-shaped event source (web: `window`). */
export interface InputEventTarget {
  addEventListener(type: string, listener: (e: any) => void, capture?: boolean): void;
  removeEventListener(type: string, listener: (e: any) => void, capture?: boolean): void;
}

/** Is the event aimed at a text field (typing a name must not move a player or navigate menus)? */
export const isTypingTarget = (target: unknown): boolean => {
  const el = target as { tagName?: string; isContentEditable?: boolean; type?: string } | null;
  if (!el || typeof el.tagName !== 'string') return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName.toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  const type = (el.type ?? 'text').toLowerCase();
  return !['button', 'checkbox', 'radio', 'range', 'submit', 'reset', 'color'].includes(type);
};

export class KeyboardState {
  private readonly down = new Set<string>();
  private readonly latched = new Set<string>();
  /** Called on every non-repeat keydown that is not typing (activity tracking, rebinding capture). */
  onPress: ((code: string, e: KeyEventLike | null) => void) | null = null;
  private readonly onDown = (e: KeyEventLike): void => {
    if (e.repeat || isTypingTarget(e.target)) return;
    this.down.add(e.code);
    this.latched.add(e.code);
    this.onPress?.(e.code, e);
  };
  private readonly onUp = (e: KeyEventLike): void => {
    this.down.delete(e.code);
  };
  private readonly onBlur = (): void => {
    this.down.clear();
    this.latched.clear();
  };

  constructor(private readonly target: InputEventTarget | null = null) {
    this.target?.addEventListener('keydown', this.onDown, true);
    this.target?.addEventListener('keyup', this.onUp, true);
    this.target?.addEventListener('blur', this.onBlur);
  }

  /** Held now, or pressed since the last `endSample()`. */
  isActive(code: string): boolean {
    return this.down.has(code) || this.latched.has(code);
  }

  isHeld(code: string): boolean {
    return this.down.has(code);
  }

  /** Clear tap latches; call once per tick after all keyboard devices sampled. */
  endSample(): void {
    if (this.latched.size > 0) this.latched.clear();
  }

  /** Test hook. */
  simulate(code: string, pressed: boolean): void {
    if (pressed) {
      if (this.down.has(code)) return;
      this.down.add(code);
      this.latched.add(code);
      this.onPress?.(code, null);
    } else this.down.delete(code);
  }

  dispose(): void {
    this.target?.removeEventListener('keydown', this.onDown, true);
    this.target?.removeEventListener('keyup', this.onUp, true);
    this.target?.removeEventListener('blur', this.onBlur);
    this.onPress = null;
  }
}

export class KeyboardDevice implements InputDevice {
  readonly kind = 'keyboard' as const;
  readonly connected = true;
  /** codes[i] = physical codes of logical button i (BUTTON_NAMES order). */
  private codes: string[][] = [];
  private _bindings!: Bindings;

  constructor(
    readonly id: string,
    readonly label: string,
    private readonly keys: KeyboardState,
    bindings: Bindings,
  ) {
    this.bindings = bindings;
  }

  get bindings(): Bindings {
    return this._bindings;
  }

  set bindings(b: Bindings) {
    this._bindings = cloneBindings(b);
    this.codes = BUTTON_NAMES.map((n) => this._bindings[n]);
  }

  /** Is `code` one of this device's game keys? */
  uses(code: string): boolean {
    for (let i = 0; i < this.codes.length; i++) if (this.codes[i]!.includes(code)) return true;
    return false;
  }

  sample(): InputFrame {
    let f = 0;
    const codes = this.codes;
    for (let i = 0; i < codes.length; i++) {
      const list = codes[i]!;
      for (let j = 0; j < list.length; j++) {
        if (this.keys.isActive(list[j]!)) {
          f |= 1 << i;
          break;
        }
      }
    }
    return f;
  }

  dispose(): void {}
}
